import { useEffect, useState } from "react";
import { Navigation, Compass, Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/hooks/use-lang";
import {
  computeNavigationReadiness,
  getNavigationReadinessLabels,
  type LatLng,
} from "@/lib/navigation-readiness";
import { GEOLOCATION_OPTIONS, describeGeolocationError } from "@/lib/geolocation-policy";
import {
  buildNavigationUrl,
  buildRoutePreviewUrl,
  copyRouteUrl,
  isValidLatLng,
  type RoutePoint,
} from "@/lib/navigation-url";
import { toast } from "sonner";

export { buildNavigationUrl };

export type NavigationReadinessBarProps = {
  /** Start point of the route (first stop coords, or an explicit origin). */
  startPoint?: LatLng | null;
  /** Fallback name used to build a search URL when coordinates are missing/invalid. */
  startPointName?: string | null;
  /** Optionally pass a known user position instead of requesting geolocation. */
  userPosition?: LatLng | null;
  /** Called when the user chooses to preview the route (e.g. scroll to map). */
  onPreview?: () => void;
  /** Ordered stops used to preview the complete route in Google Maps. */
  routeStops?: RoutePoint[];
  mode?: string;
  /** Set to false when the Google navigation button/service is known to be unavailable. */
  googleNavAvailable?: boolean;
  className?: string;
};

/**
 * Small reusable bar that tells the user whether they can start navigating
 * from where they are, or should first head to the route's starting point.
 * Never blocks previewing the route: geolocation is requested once,
 * non-blockingly, and any denial/timeout degrades to the preview action.
 */
export function NavigationReadinessBar({
  startPoint,
  startPointName,
  userPosition,
  onPreview,
  googleNavAvailable,
  className,
  routeStops = [],
  mode,
}: NavigationReadinessBarProps) {
  const { lang } = useLang();
  const [detectedPosition, setDetectedPosition] = useState<LatLng | null>(null);
  const [opening, setOpening] = useState<"preview" | "navigate" | null>(null);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);

  const effectivePosition = userPosition ?? detectedPosition;

  useEffect(() => {
    if (userPosition !== undefined) return; // caller manages position
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setDetectedPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        if (cancelled) return;
        describeGeolocationError(err);
      },
      GEOLOCATION_OPTIONS,
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const state = computeNavigationReadiness({
    userPosition: effectivePosition,
    startPoint: isValidLatLng(startPoint) ? startPoint : null,
    googleNavAvailable,
  });
  const labels = getNavigationReadinessLabels(state, lang);

  const navUrl = buildNavigationUrl({
    userPosition: effectivePosition,
    startPoint,
    startPointName,
  });
  const previewUrl = buildRoutePreviewUrl({ stops: routeStops, mode });

  function openGoogleUrl(url: string, action: "preview" | "navigate") {
    setOpening(action);
    setFallbackUrl(null);
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    window.setTimeout(() => {
      setOpening(null);
      if (!opened) {
        setFallbackUrl(url);
        toast.info("O navegador bloqueou a nova janela. A abrir o percurso nesta página…");
        window.location.assign(url);
      }
    }, 350);
  }

  const showPrimaryAsNav =
    state.status === "ready_to_start" || state.status === "far_from_start";

  return (
    <div
      className={`rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-soft)] ${className ?? ""}`}
    >
      {labels.message && (
        <p className="text-sm text-muted-foreground">{labels.message}</p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {labels.primaryAction && showPrimaryAsNav && navUrl && (
          <Button size="sm" className="gap-1.5" disabled={opening !== null} onClick={() => openGoogleUrl(navUrl, "navigate")}>
            {opening === "navigate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />} {labels.primaryAction}
          </Button>
        )}
        {labels.secondaryAction && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={opening !== null || (!previewUrl && !onPreview)}
            onClick={() => {
              if (previewUrl) openGoogleUrl(previewUrl, "preview");
              else onPreview?.();
            }}
          >
            {opening === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Compass className="h-4 w-4" />} {labels.secondaryAction}
          </Button>
        )}
        {fallbackUrl && (
          <Button type="button" variant="ghost" size="sm" onClick={async () => {
            const copied = await copyRouteUrl(fallbackUrl);
            toast[copied ? "success" : "error"](copied ? "Ligação do percurso copiada." : "Não foi possível copiar a ligação.");
          }}>
            <Copy className="h-4 w-4" /> Copiar ligação do Google Maps
          </Button>
        )}
      </div>
    </div>
  );
}
